<template>
    <b-container fluid class="overview">
        <h1 class="text-left">{{ $t('menuItemThemes') }}</h1>
        <br />
        <div class="row">
            <!-- Search field -->
            <search-box
                id="searchThemes"
                @input="$_onInput"
                :info="$t('searchInfo')"
                :autofocus="true"
                :value="searchText"
                :saveInState="true"
            >
            </search-box>
            <!-- Themelayer filter field -->
            <div class="col-md-3 pl-0">
                <x-select
                    :id="`selectThemelayer`"
                    :name="`selectThemelayer`"
                    @selected="$_onSelectThemelayer"
                    :options="themelayers"
                    :textDefaultOption="$t('themelayerFilter')"
                    :value="selectedThemelayer"
                    :saveInState="true"
                >
                </x-select>
            </div>
            <!-- Themegroup filter field -->
            <div class="col-md-2 pl-0">
                <x-select
                    :id="`selectThemeGroup`"
                    :name="`selectThemeGroup`"
                    @selected="$_onSelectThemeGroup"
                    :options="themeGroups"
                    :textDefaultOption="$t('themeGroups.filter')"
                    :value="selectedThemeGroup"
                    :saveInState="true"
                >
                </x-select>
            </div>
            <b-link class="px-1 pt-2" @click="$_clearFilters()">
                {{ $t('clearFilters') }}
            </b-link>
        </div>
        <!-- Overview Table -->
        <div>
            <b-table
                id="themeoverview"
                striped
                hover
                small
                outlined
                bordered
                v-show="dataLoaded"
                @row-clicked="$_onDetails"
                :sort-by.sync="sortBy"
                :tbody-tr-class="$_rowClass"
                :items="filteredRecords"
                :fields="fields"
                sort-icon-left
            >
                <template v-slot:cell(isUnavailable)="data">
                    <em
                        title="thema niet beschikbaar"
                        class="error"
                        :class="data.value"
                    ></em>
                </template>
            </b-table>
        </div>
        <!-- No content for overview -->
        <div v-show="noThemesToshow" class="col-md-12">
            <span> &nbsp; {{ $t('themesNotPresent') }}</span>
        </div>
        <router-links-footer :routerLinks="[{ name: 'themesDetailNew' }]" />
    </b-container>
</template>

<script>
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import SearchBox from '../../components/SearchBox.vue';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import RouterLinksFooter from '../../components/dashboard/RouterLinksFooter.vue';
    import { orderBy } from 'lodash';
    import {
        arraySortAscendingFunction,
        nprogress
    } from '../../services/utilities';

    export default {
        mixins: [modalMixin],

        components: {
            'search-box': SearchBox,
            'x-select': XSelect,
            'router-links-footer': RouterLinksFooter
        },

        data() {
            return {
                searchText: '',
                fields: [
                    {
                        key: 'name',
                        label: this.$t('columnNameName'),
                        sortable: true,
                        stickyColumn: true,
                        class: 'text-left',
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'themeLayerTitlesConcatenated',
                        label: this.$t('menuItemThemeLayers'),
                        sortable: true,
                        class: 'text-left',
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'themeGroup.name',
                        label: this.$t('themeGroups.title'),
                        sortable: true,
                        tdClass: 'w-25',
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'isUnavailable',
                        label: '',
                        sortable: false,
                        class: 'text-right',
                        thStyle: { border: 0 }
                    }
                ],
                sortBy: 'name',
                selectedThemelayer: '',
                selectedThemeGroup: '',
                records: [],
                themelayers: [],
                themeGroups: [],
                noThemesToshow: false
            };
        },

        computed: {
            dataLoaded() {
                return this.records.length > 0;
            },

            filteredRecords() {
                let records = this.records;
                // filter records on NAME
                if (this.searchText.length > 0) {
                    records = records.filter((record) => {
                        return record.name
                            .toLowerCase()
                            .includes(this.searchText.toLowerCase());
                    });
                }
                // filter records on themelayer name
                if (this.selectedThemelayer && this.selectedThemelayer.length > 0) {
                    records = records.filter((record) => {
                        return record.themeLayerTitles.some(
                            (t) =>
                                t.toLowerCase() ===
                                this.selectedThemelayer.toLowerCase()
                        );
                    });
                }
                // filter records on themegroup name
                if (this.selectedThemeGroup && this.selectedThemeGroup.length > 0) {
                    records = records.filter((record) => {
                        return (
                            record.themeGroup?.name.toLowerCase() ===
                            this.selectedThemeGroup.toLowerCase()
                        );
                    });
                }
                // set error icons
                records.forEach((record) => {
                    if (record.unavailable) {
                        record.isUnavailable = 'fas fa-exclamation-triangle';
                    } else {
                        record.isUnavailable = null;
                    }
                });
                return records;
            }
        },

        mounted() {
            this.$_getThemes();
        },

        methods: {
            async $_getThemes() {
                nprogress.start();
                try {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetThemeOverview'
                    });
                    this.noThemesToshow = result?.data.length === 0;
                    this.records = Object.assign([], result?.data);
                    // fill array with all themelayer titles
                    this.records.forEach((record) => {
                        // Add themegroups
                        if (record.themeGroup) {
                            const themeGroup = this.themeGroups.find(
                                ({ value }) => value === record.themeGroup.name
                            );
                            if (!themeGroup) {
                                this.themeGroups.push({
                                    value: record.themeGroup.name,
                                    text: record.themeGroup.name
                                });
                            }
                            this.themeGroups?.sort(
                                arraySortAscendingFunction('text', false)
                            );
                        }
                        // Add concatenated themelayers, sorted alphabetically
                        record.themeLayerTitlesConcatenated = orderBy(
                            record.themeLayerTitles,
                            [(t) => t.toLowerCase()]
                        ).join(', ');
                        if (
                            record.themeLayerTitles &&
                            record.themeLayerTitles.length > 0
                        ) {
                            record.themeLayerTitles.forEach((title) => {
                                this.themelayers.push({
                                    value: title,
                                    text: title
                                });
                            });
                        }
                    });
                    // Remove duplicates and sort records (lodash)
                    if (this.themelayers.length > 0) {
                        this.themelayers = this.themelayers.filter(
                            (elem, index, self) =>
                                self.findIndex((t) => {
                                    return t.text === elem.text;
                                }) === index
                        );

                        this.themelayers = orderBy(this.themelayers, [
                            (t) => t.text.toLowerCase()
                        ]);
                    }
                    nprogress.done();
                } catch (error) {
                    nprogress.done();
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                }
            },

            $_onDetails(record) {
                this.$router.push({ path: `/dashboard/theme/detail/${record.id}` });
            },

            $_onInput(searchText) {
                this.searchText = searchText;
            },

            $_onSelectThemelayer(input) {
                this.selectedThemelayer = input;
            },

            $_onSelectThemeGroup(input) {
                this.selectedThemeGroup = input;
            },

            $_rowClass() {
                return 'tablerow';
            },

            $_clearFilters() {
                this.$_onInput('');
                this.$_onSelectThemelayer(null);
                this.$_onSelectThemeGroup(null);
            }
        }
    };
</script>

<style>
.error {
    color: red;
}
/*Following classes together remove the vertical line
        between the last two cells in each row
    */
#themeoverview.table-bordered td:nth-child(2) {
    border-right-width: 0px;
}

#themeoverview.table-bordered td:last-child {
    border-left-width: 0px;
}
</style>
